/**
 * Kurumsal onay kuyrukları — Pending{Flight,Hotel,RentCar,Transfer,Bus} — migration guide
 * Bölüm 3.8 / 4.12 / 6.2. All five product routes are structurally identical (confirmed via
 * swagger + curl — see api/types/approvals.ts header for the full evidence; `Bus` added
 * 2026-09-09), so a single generic pair of functions drives all five instead of five
 * near-duplicate implementations.
 */
import { apiPost, apiPostSession } from '../client';
import type {
  EditPendingApprovalRequest,
  PendingApprovalItem,
  PendingApprovalProductType,
  PendingApprovalQueue,
  PendingApprovalRawResponse,
} from '../types/approvals';

/** `Flight` -> `PendingFlight`, `RentCar` -> `PendingRentCar`, etc. — the controller name. */
function routePrefix(productType: PendingApprovalProductType): string {
  return `Pending${productType}`;
}

/** `Flight` -> `pendingFlight`, `RentCar` -> `pendingRentCar` — the request/response field
 *  prefix. `pending${productType}` is already valid camelCase since `pending` starts lowercase. */
function fieldPrefix(productType: PendingApprovalProductType): string {
  return `pending${productType}`;
}

/** GetPending route uses the plural product noun: `Flights` / `Hotels` / `RentCars` /
 *  `Transfers` / `Buses` (Bus → Buses, not "Buss"). */
function pluralProduct(productType: PendingApprovalProductType): string {
  return productType === 'Bus' ? 'Buses' : `${productType}s`;
}

function normalizeQueue(
  response: PendingApprovalRawResponse,
  productType: PendingApprovalProductType
): PendingApprovalQueue {
  const prefix = fieldPrefix(productType);
  const waitingApproval = response[`${prefix}WaitingApproval`];
  const willApproved = response[`${prefix}WillApproved`];
  return {
    waitingApproval: Array.isArray(waitingApproval) ? (waitingApproval as PendingApprovalItem[]) : [],
    willApproved: Array.isArray(willApproved) ? (willApproved as PendingApprovalItem[]) : [],
  };
}

/** GetPending{Flights,Hotels,RentCars,Transfers} — bare-session body (see type file header). */
export async function getPendingApprovals(
  productType: PendingApprovalProductType
): Promise<PendingApprovalQueue> {
  const route = `${routePrefix(productType)}/GetPending${pluralProduct(productType)}`;
  const response = await apiPostSession<PendingApprovalRawResponse>(route);
  return normalizeQueue(response, productType);
}

/** EditPending{Flight,Hotel,RentCar,Transfer} — approve or deny a single pending item.
 *  `apiPost`'s default `{...body, sessionHeader: session}` merge is exactly the swagger-
 *  confirmed `Pending{X}Request` shape (`{ sessionHeader, pending{X} }`) — no override needed. */
export async function editPendingApproval(
  productType: PendingApprovalProductType,
  payload: EditPendingApprovalRequest
): Promise<PendingApprovalQueue> {
  const route = `${routePrefix(productType)}/EditPending${productType}`;
  const body = { [fieldPrefix(productType)]: payload };
  const response = await apiPost<typeof body, PendingApprovalRawResponse>(route, body);
  return normalizeQueue(response, productType);
}
